import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Stack } from "@mui/material";

import { Button as GradientButton } from "@ui/index";

import { logout, my } from "@store/actions/user";
import instance from "@store/middleware/api";

import { ERoutes } from "@utils/constants";
import styled, { css, keyframes } from "styled-components";

import { ButtonLabel } from "@/components/ButtonGlyph";
import {
  BottomFade,
  ScrollArea,
  ScrollChevron,
  ScrollHint,
  useScrollAffordance,
} from "@/components/CartModal/phoneTemplate";
import { CheckboxBox, CheckboxLabel, CheckboxRow } from "@/components/CartModal/styles";
import SignUpDialog from "@/components/SignUpDialog";
import { ESignUp } from "@/layouts/SecondRootLayout";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

/**
 * First-login confirmation for accounts carried over from the old site.
 *
 * A migrated customer agreed to the OLD site's terms, in most cases years ago.
 * All four public documents were rewritten for the relaunch, so that agreement
 * no longer describes what they are using. This asks once, records it exactly
 * as a registration would, and gets out of the way.
 *
 * DELIBERATELY NOT DISMISSIBLE — the one modal in the app with no close button,
 * no overlay click and no Escape. Nothing behind it may be used until the
 * question is answered, and `modal-architecture.md` rule 13 already forbids
 * overlay-close everywhere; here the X goes too. The server enforces the same
 * rule independently (LegalReconsentMiddleware), so closing this in devtools
 * buys nothing.
 *
 * The way out is leaving, not dismissing: Logout. A gate with no exit at all
 * would leave someone who does not want to agree unable even to get out.
 *
 * Follows the three-zone layout (header / scrolling content / footer) and the
 * shared checkbox + button styling from the create-account modal, so it reads
 * as the same product rather than a one-off.
 */

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  /* Transparent — the dimming is the card's box-shadow, so the gradient
     surface stays fully visible (modal-architecture.md §2). */
  background: transparent;
`;

const ModalCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 520px;
  max-height: 85dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: none;
  border-radius: 24px;
  color: #ffffff;
  box-shadow:
    0 0 0 9999px rgba(0, 0, 0, 0.7),
    0 20px 60px rgba(0, 0, 0, 0.6);
  animation: ${slideUp} 0.25s ease both;
  /* The shared static modal gradient — modal-architecture.md §2. Kept in step
     with design-system.ts components.modal.background. */
  background:
    radial-gradient(ellipse 45% 40% at 42% -3%, rgba(176, 144, 105, 0.9) 0%, rgba(160, 128, 90, 0.5) 35%, transparent 75%),
    radial-gradient(ellipse 55% 50% at 58% 3%, rgba(170, 140, 100, 0.75) 0%, rgba(140, 110, 78, 0.35) 45%, transparent 85%),
    radial-gradient(ellipse 70% 35% at 50% 8%, rgba(150, 118, 82, 0.4) 0%, transparent 70%),
    linear-gradient(180deg, rgb(153, 112, 75) 0%, rgb(99, 74, 54) 50%, rgb(111, 83, 60) 100%);

  @media (max-width: 600px) {
    max-height: 88dvh;
    border-radius: 18px;
  }
`;

/* ── Zone 1: header. Never scrolls. ── */
const ModalHead = styled.div`
  flex-shrink: 0;
  padding: 28px 40px 0;

  h2 {
    margin: 0;
    font-family: "Cormorant Garamond", serif;
    font-size: 2rem;
    font-weight: 500;
    line-height: 1.2;
    color: #ffffff;
    text-align: center;
  }

  @media (max-width: 600px) {
    padding: 22px 24px 0;

    h2 {
      font-size: 1.6rem;
    }
  }
`;

const Divider = styled.div`
  margin: 16px 0 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(235, 194, 142, 0.45), transparent);
`;

/* ── Zone 2: the only scrolling area. Right padding is 12px short of the left
   so the reserved scrollbar gutter brings it back to level with the footer
   (modal-architecture.md §5). ── */
const ModalBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 28px 8px 40px;
  scrollbar-gutter: stable;

  &::-webkit-scrollbar {
    width: 12px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(235, 194, 142, 0.5);
    border-radius: 6px;
    border: 4px solid transparent;
    background-clip: padding-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(235, 194, 142, 0.7);
    background-clip: padding-box;
  }

  @media (max-width: 600px) {
    padding: 16px 12px 8px 24px;
  }
`;

const Intro = styled.p`
  margin: 0 0 16px;
  font-family: "Raleway", sans-serif;
  font-size: 0.95rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.88);

  @media (max-width: 600px) {
    font-size: 0.88rem;
  }
`;

/* ── Zone 3: footer. No background of its own — it shares the modal surface. ── */
const ModalFooter = styled.div`
  flex-shrink: 0;
  padding: 8px 40px 28px;

  @media (max-width: 600px) {
    padding: 8px 24px 22px;
  }
`;

const shakeKeyframes = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
`;

const ShakeBlock = styled.div<{ $shake: boolean; $areaInvalid?: boolean }>`
  ${({ $shake }) =>
    $shake &&
    css`
      animation: ${shakeKeyframes} 0.4s ease-in-out;
    `}

  ${({ $areaInvalid }) =>
    $areaInvalid &&
    css`
      border-radius: 8px;
      box-shadow: 0 0 0 1px #e47a6a;
    `}
`;

/* Same compaction the create-account modal applies to these shared rows on a
   phone, so the two read identically at small widths. */
const ReviewCompact = styled.div`
  width: 100%;

  @media (max-width: 600px) {
    ${CheckboxRow} {
      padding: 10px 14px;
      gap: 10px;
      margin-bottom: 8px;
    }
    ${CheckboxBox} {
      width: 20px;
      height: 20px;
    }
    ${CheckboxLabel} {
      font-size: 0.78rem;
    }
  }
`;

const Tick = () => (
  <svg viewBox="0 0 12 12" fill="none">
    <path d="M2 6L5 9L10 3" stroke="#3c2d23" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Exit = styled.div`
  margin-top: 14px;
  text-align: center;
  font-family: "Raleway", sans-serif;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.6);

  button {
    color: rgba(255, 255, 255, 0.85);
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    text-decoration: none;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: #ffffff;
      text-decoration: underline;
    }
  }
`;

const Err = styled.p`
  margin: 12px 0 0;
  font-family: "Raleway", sans-serif;
  font-size: 0.85rem;
  color: #e47a6a;
  text-align: center;
`;

export const LegalReconsentGate: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const required = !!(user as any)?.user?.requiresLegalReconsent;

  const [agreed, setAgreed] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [shake, setShake] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Shown after a successful confirm. Same dialog a new registration ends
  // on — a migrated customer is arriving at the rebuilt site for the first
  // time too, so they get the same "here is where to go" moment.
  const [showWelcome, setShowWelcome] = useState(false);

  const bodyRef = useRef<HTMLDivElement>(null);
  const canScrollDown = useScrollAffordance(bodyRef, [required]);

  // Once confirmed the flag clears, but the welcome still has to be shown —
  // so the component keeps rendering until that is dismissed too.
  if (!required && !showWelcome) return null;

  const handleLogout = async (): Promise<void> => {
    try {
      const tokens = JSON.parse(localStorage.getItem("tokens") || "{}");
      await dispatch(logout({ accessToken: tokens?.accessToken || "", refreshToken: tokens?.refreshToken || "" }));
    } catch {
      /* non-fatal — leaving is the point; a failed revoke must not strand them */
    }
    window.location.href = ERoutes.Home;
  };

  const submit = async (): Promise<void> => {
    // Same feedback as the create-account modal: the row shakes and takes a red
    // outline rather than growing an error line that shifts everything below.
    if (!agreed) {
      setInvalid(true);
      setShake(false);
      requestAnimationFrame(() => setShake(true));
      return;
    }
    setSaving(true);
    setError("");
    try {
      await instance.post("/users/my/legal-reconsent", { newsletterAccepted: newsletter });
      // Welcome goes up BEFORE the re-read. `my()` clears `required`, and if the
      // welcome flag were not already set the component would unmount in that
      // same commit and the dialog would never appear.
      setShowWelcome(true);
      // Re-read the account rather than patching the flag locally: the gate
      // renders off the server's answer, so it closes only once the server
      // actually agrees it should.
      await dispatch(my());
    } catch {
      setError("That didn't save. Please check your connection and try again.");
      setSaving(false);
    }
  };

  if (showWelcome) {
    return (
      <SignUpDialog
        open
        step={ESignUp.Welcome}
        handleClose={() => setShowWelcome(false)}
        handleNextStep={() => setShowWelcome(false)}
      />
    );
  }

  return createPortal(
    <Overlay role="dialog" aria-modal="true" aria-labelledby="reconsent-title">
      <ModalCard>
        <ModalHead>
          <h2 id="reconsent-title">Confirmation needed</h2>
          <Divider />
        </ModalHead>

        <ScrollArea>
          <ModalBody ref={bodyRef}>
            <Intro>
              Audio Sanctum was completely rebuilt. Your account and favorites are still in place.
              Please agree to our new terms below if you want to continue.
            </Intro>

            <ReviewCompact>
              <Stack spacing="4px">
                {/* The required consent leads, the optional one follows. This is
                    the reverse of the create-account modal, where marketing goes
                    first — there the terms box is the last thing before the
                    button, and here the whole modal exists FOR the terms, so
                    burying them under a marketing row reads wrong. */}
                <ShakeBlock $shake={shake} $areaInvalid={invalid} onAnimationEnd={() => setShake(false)}>
                  <CheckboxRow
                    onClick={() => {
                      setAgreed((v) => !v);
                      setInvalid(false);
                    }}
                    style={{ background: "transparent", border: "1px solid transparent" }}
                  >
                    <CheckboxBox $checked={agreed}>
                      <Tick />
                    </CheckboxBox>
                    <CheckboxLabel>
                      I have read and agree to the{" "}
                      <a
                        href={ERoutes.Conditions}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Terms of Use
                      </a>
                      ,{" "}
                      <a
                        href={ERoutes.Privacy}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Privacy Policy
                      </a>
                      ,{" "}
                      <a
                        href={ERoutes.RefundPolicy}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Refund Policy
                      </a>{" "}
                      and{" "}
                      <a
                        href={ERoutes.LicenceTerms}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        YouTube Licence Terms
                      </a>
                      .
                    </CheckboxLabel>
                  </CheckboxRow>
                </ShakeBlock>

                <CheckboxRow
                  onClick={() => setNewsletter((v) => !v)}
                  style={{
                    background: "rgba(235, 194, 142, 0.1)",
                    border: "1px dashed rgba(235, 194, 142, 0.5)",
                    marginBottom: "4px",
                  }}
                >
                  <CheckboxBox $checked={newsletter}>
                    <Tick />
                  </CheckboxBox>
                  <CheckboxLabel>Send me emails about new released music and offers.</CheckboxLabel>
                </CheckboxRow>
              </Stack>
            </ReviewCompact>
          </ModalBody>
          <BottomFade $visible={canScrollDown} aria-hidden="true">
            <ScrollHint>
              <span>Scroll</span>
              <ScrollChevron />
            </ScrollHint>
          </BottomFade>
        </ScrollArea>

        <ModalFooter>
          <GradientButton
            isBigButton
            isFullWidth
            isDisabled={saving}
            // Grey-but-clickable until the box is ticked, so the click shakes
            // what's missing instead of doing nothing — the create-account rule.
            isDimmed={!agreed}
            onClick={submit}
          >
            {saving ? (
              "Confirming…"
            ) : (
              <ButtonLabel glyph="arrowRight" muted={!agreed}>
                Confirm and continue
              </ButtonLabel>
            )}
          </GradientButton>
          {!!error && <Err>{error}</Err>}
          <Exit>
            <button type="button" onClick={handleLogout}>
              Logout
            </button>
          </Exit>
        </ModalFooter>
      </ModalCard>
    </Overlay>,
    document.body,
  );
};

export default LegalReconsentGate;
